import { Navigate, useParams } from "react-router-dom";
import { useAuth } from "../context/authContext";
import { getContentTypeSlug, getTrackPath } from "../utils/utils";
import { getLastTrackPath } from "../utils/lastTrackPath.js";

/**
 * Resolves the Track URLs that don't name a library tab yet:
 *
 * - /track, and /track/u/<you>: your own library — where you last left it this
 *   session (see lastTrackPath.js), otherwise on your Track defaults: All or
 *   your primary interest, and your default status.
 * - /track/u/<someone else>: their library on All, finished. Your preferences
 *   describe how you land on your own page, not theirs, and All doesn't assume
 *   which types they track.
 * - /track signed out: the demo library.
 */
export default function TrackRedirect() {
  const { user } = useAuth();
  const { username } = useParams();

  if (username && username !== user?.username) {
    return <Navigate to={getTrackPath(username, "all", "finished")} replace />;
  }

  if (!user) {
    return <Navigate to={getTrackPath("demo", "movie", "finished")} replace />;
  }

  const lastTrackPath = getLastTrackPath(user.username);
  if (lastTrackPath) {
    return <Navigate to={lastTrackPath} replace />;
  }

  // The primary interest falls back to movies if it has no URL slug — e.g. a
  // content type that was retired, or one that only exists for collections
  // ("mixed"). Without this the path would point at a content type Track
  // doesn't have.
  const primaryContent = getContentTypeSlug(user.preferences?.content)
    ? user.preferences.content
    : "movie";
  const content =
    user.preferences?.trackContent === "primary" ? primaryContent : "all";
  return (
    <Navigate
      to={getTrackPath(
        user.username,
        content,
        user.preferences?.status || "finished",
      )}
      replace
    />
  );
}
